import React from 'react';
import { Loader2 } from 'lucide-react';

interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Loading message to display */
  message?: string;
  /** Show as fullscreen overlay */
  fullscreen?: boolean;
  /** Optional progress percentage (0-100) */
  progress?: number;
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12',
};

/**
 * Reusable loading spinner component
 * Shows animated spinner with optional message and progress bar
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  message,
  fullscreen = false,
  progress,
  className = '',
}) => {
  const spinnerSize = sizeClasses[size];

  const content = (
    <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
      {/* Spinner */}
      <Loader2 className={`${spinnerSize} animate-spin text-primary`} />

      {/* Message */}
      {message && (
        <p className="text-sm text-muted-foreground text-center max-w-xs">
          {message}
        </p>
      )}

      {/* Progress Bar */}
      {progress !== undefined && progress >= 0 && progress <= 100 && (
        <div className="w-48 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Progress Percentage */}
      {progress !== undefined && progress >= 0 && progress <= 100 && (
        <p className="text-xs font-medium text-muted-foreground">
          {Math.round(progress)}%
        </p>
      )}
    </div>
  );

  if (fullscreen) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
        {content}
      </div>
    );
  }

  return content;
};

/**
 * Skeleton loading component for graph placeholder
 */
export const SkeletonGraph: React.FC = () => {
  return (
    <div className="w-full h-full flex items-center justify-center bg-muted/20 animate-pulse">
      <div className="space-y-8 w-full max-w-2xl p-8">
        {/* Fake nodes */}
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center justify-center">
            <div className="w-72 h-24 bg-muted rounded-lg shadow-sm">
              <div className="p-4 space-y-2">
                <div className="h-4 bg-muted-foreground/20 rounded w-3/4" />
                <div className="h-3 bg-muted-foreground/10 rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Loading message overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-card/90 backdrop-blur-sm border border-border rounded-lg shadow-lg p-6">
          <LoadingSpinner size="lg" message="Rendering workflow graph..." />
        </div>
      </div>
    </div>
  );
};

/**
 * Skeleton loading component for detail panel
 */
export const SkeletonPanel: React.FC = () => {
  return (
    <div className="w-full h-full p-6 space-y-6 animate-pulse">
      {/* Header */}
      <div className="space-y-2">
        <div className="h-6 bg-muted rounded w-32" />
        <div className="h-8 bg-muted rounded w-3/4" />
        <div className="h-4 bg-muted rounded w-1/2" />
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="h-3 bg-muted rounded w-16" />
          <div className="h-4 bg-muted rounded w-24" />
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-muted rounded w-16" />
          <div className="h-4 bg-muted rounded w-28" />
        </div>
      </div>

      {/* Configuration section */}
      <div className="space-y-3">
        <div className="h-5 bg-muted rounded w-32" />
        <div className="space-y-2 bg-muted/30 rounded-lg p-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-1">
              <div className="h-3 bg-muted rounded w-24" />
              <div className="h-4 bg-muted rounded w-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Inline loading state for buttons/actions
 */
export const ButtonLoading: React.FC<{ text?: string }> = ({ text = 'Loading...' }) => {
  return (
    <span className="inline-flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" />
      {text}
    </span>
  );
};
